import React, { useState, useEffect, KeyboardEvent, useRef } from "react";
import NavBar from "../components/NavBar";
import AbstractButton from "../components/AbstractButton";
import { ReactComponent as SortIcon } from "../../assets/icons/sort-filter.svg?react";
import { ReactComponent as SaveIcon } from "../../assets/icons/save-icon.svg?react";
import { ReactComponent as CompleteIcon } from "../../assets/icons/checkbox-checked-regular.svg?react";
import { ReactComponent as BackIcon } from "../../assets/icons/back-chevron.svg?react";
import { ReactComponent as ScanIcon } from "../../assets/icons/barcode-scanner.svg?react";
import ForegroundColorSet from "../../utilities/enums/ForegroundColorSet";
import BackgroundColorSet from "../../utilities/enums/BackgroundColorSet";
import { useLocation, useNavigate } from "react-router-dom";
import TabList, { TabProps } from "../components/TabList";
import StockExtractLineCard from "./StockExtractLineCard";
import { StockCheckExtract, StockCheckExtractLine } from "../../interfaces/Sce";
import {
  defaultStockCheckLinesSearch,
  StockCheckLinesSearch,
  StockLineSearchOrderOption,
  StockCheckSavedDataSearch,
} from "../../interfaces/SceSearch";
import { apiFetch } from "../../utilities/AuthenticatedFetch";
import StockExtractFinishPage from "./StockExtractFinishPage";
import { useDispatch, useSelector } from "react-redux";
import {
  setPartCountProgress,
  StockSliceSavedCheck,
  StockSliceCountedLine,
} from "../../store/slices/stockcheckslice";
import { RootState } from "../../store/store";
import StockUnsavedChanges from "./StockUnsavedChanges";
import { Slide, toast, ToastContainer } from "react-toastify";
import StockCheckLineFilter from "./StockCheckLineFilter";
import { Inclusivity, OrderDirection } from "../../interfaces/PartSearch";
import StockLoadingLines from "./StockLoadingLines";
import ScrollableLargeList from "../components/ScrollableLargeList";

const StockExtract = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const fetchAmount = 10000;
  const customToastId = "custom-toast-warn-id";

  let savedDataSearchParams: StockCheckSavedDataSearch;

  const [parentExtract, setParentExtract] = useState<StockCheckExtract>();
  const [finishLines, setFinishLines] = useState<StockCheckExtractLine[]>([]);
  const [beyondReturnLimit, setBeyondReturnLimit] = useState(false);
  const [retryInit, setRetryInit] = useState(0);
  const [showFinishPage, setShowFinishPage] = useState(false);
  const [showUnsavedChangesBox, setShowUnsavedChangesBox] = useState(false);
  const [totalLinesCounted, setTotalLinesCounted] = useState(0);
  const [targetExitNavigation, setTargetExitNavigation] = useState("");
  const [searchAndFilterMenuVisible, setFilterMenuVisible] = useState(false);
  const [showSearchAndFilterMenuTrigger, setShowFilterMenuTrigger] =
    useState(0);

  const rangeRef = useRef({ start: 0, end: fetchAmount });
  const lastRecordRef = useRef("");
  const isFetchingRef = useRef(false);
  const parentExtractRef = useRef("");
  const depotIdRef = useRef("");
  const [isSavedDataLoaded, setIsSavedDataLoaded] = useState(false);
  const [linesCollection, setLinesCollection] = useState<
    StockCheckExtractLine[]
  >([]);
  const linesCollectionPartNosRef = useRef<string[]>([]);
  const scLinesSearchRef = useRef(defaultStockCheckLinesSearch);
  const [searchTerm, setSearchTerm] = useState("");
  const [initialLoadingFinished, setInitialLoadingFinished] = useState(false);

  const fetchingLinesCollectionRef = useRef<StockCheckExtractLine[]>([]);
  const allLinesCollectionRef = useRef<StockCheckExtractLine[]>([]);
  const [displayLines, setDisplayLines] = useState<StockCheckExtractLine[]>([]);
  const orderDirectionRef = useRef<OrderDirection>(OrderDirection.Ascending);

  const [showLoadingLinesPage, setShowLoadingLinesPage] = useState(false);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const progressTotalLines = useRef(0);

  const SEARCH_ENDPOINT = "/partsapp/stock/LoadStockCheckLines";
  const SAVED_DATA_ENDPOINT = "/partsapp/stock/GetStockCheckDataForParts";
  const systemSavedAllChecks = useSelector(
    (state: RootState) => state.root.stock.savedChecks,
  );

  const tabList: TabProps[] = [
    {
      index: 0,
      label: "Items",
      callback: () => {
        return;
      },
      enabled: true,
    },
    {
      index: 1,
      label: "Info",
      callback: () => {
        return;
      },
      enabled: false,
    },
  ];

  const handleBackPress = () => {
    preNavAway("/stockcheck");
  };

  const SearchAndFilterClasses = searchAndFilterMenuVisible
    ? "modal-open"
    : "modal-closed";

  const updateFilter = (data: StockCheckLinesSearch) => {
    //new code
    setFilterMenuVisible(!searchAndFilterMenuVisible);
    scLinesSearchRef.current = data;

    if (scLinesSearchRef.current.OrderDirection != orderDirectionRef.current) {
      orderDirectionRef.current = scLinesSearchRef.current.OrderDirection;
    }
  };

  const cancelFilterButtonClicked = () => {
    setFilterMenuVisible(false);
  };
  const showSortandFilter = () => {
    setShowFilterMenuTrigger(showSearchAndFilterMenuTrigger + 1);
    setFilterMenuVisible(!searchAndFilterMenuVisible);
  };

  const handleCompletePress = () => {
    setShowFinishPage((prev) => !prev);
  };

  useEffect(() => {
    const extractObj = location.state.sce as StockCheckExtract;
    setParentExtract(extractObj);
    parentExtractRef.current = extractObj.stockCheckExtractRef;
    depotIdRef.current = extractObj.depotID;
    getSavedDataFromBackend();
    if (totalLinesCounted == 0) {
      setTotalLinesCounted(extractObj.linesCounted ?? 0);
    }
  }, [location]);

  useEffect(() => {
    if (isSavedDataLoaded) {
      initialGetLines();
    }
  }, [retryInit]);

  function initialGetLines() {
    if (parentExtract) {
      rangeRef.current = { start: 0, end: fetchAmount };
      linesCollectionPartNosRef.current = [];
      lastRecordRef.current = "";
      setBeyondReturnLimit(false);
      setLinesCollection([]);

      allLinesCollectionRef.current = [];
      setDisplayLines([]);

      if (fetchAmount < parentExtract.totalLines) {
        setShowProgressBar(true);
        progressTotalLines.current = parentExtract.totalLines;
      }

      getLines(rangeRef.current.start, parentExtract, scLinesSearchRef.current);
    } else {
      setRetryInit(retryInit + 1);
    }
  }

  const currentDepotId = useSelector(
    (state: RootState) => state.root.auth.currentDepotId,
  );

  const currentCompanyId = useSelector(
    (state: RootState) => state.root.auth.companyId,
  );

  const URL = "url";
  const currentUrl = localStorage.getItem(URL) ?? "";

  const handleSavePress = () => {
    // stock slice saving
    if (parentExtract) {
      const updatedStockLines: StockSliceSavedCheck = {
        stockCheckRef: parentExtract.stockCheckExtractRef,
        countedLines: finishLines.map((e) => {
          return {
            partNo: e.partNo,
            locallyCounted: e.qtySubmitted,
            previousSystemProvided: e.systemProvided,
            partDescription: e.partDescription ?? "",
            binLocation: e.binLocation ?? "",
          };
        }),
        companyId: currentCompanyId,
        depot: currentDepotId,
        url: currentUrl,
      };
      dispatch(setPartCountProgress(updatedStockLines));
      finishLines.map((e) => {
        e.hasUnsavedChanges = false;
        return e;
      });
      toast.success("Data Saved Successfully!", {
        position: "bottom-center",
        autoClose: 2000,
        theme: "colored",
        transition: Slide,
      });
    }
  };

  async function getSavedDataFromBackend() {
    if (parentExtractRef.current) {
      if (
        systemSavedAllChecks.find((slice: StockSliceSavedCheck) => {
          return (
            slice.stockCheckRef == parentExtractRef.current &&
            slice.companyId == currentCompanyId &&
            slice.depot == currentDepotId &&
            slice.url == currentUrl
          );
        })
      ) {
        const sliceData: StockSliceSavedCheck = systemSavedAllChecks.find(
          (slice: StockSliceSavedCheck) => {
            return (
              slice.stockCheckRef == parentExtractRef.current &&
              slice.companyId == currentCompanyId &&
              slice.depot == currentDepotId &&
              slice.url == currentUrl
            );
          },
        );

        const countedLines: StockSliceCountedLine[] = sliceData.countedLines;

        if (countedLines) {
          // send a request to the endpoint
          savedDataSearchParams = {
            stockCheckRef: ("00000" + parentExtractRef.current).slice(-5),
            // LeftPad the StockCheckRef to 5 chars with leading 0s so that SerialisedIsam fetches are not inhibited.
            depotId: depotIdRef.current,
            canSeeQoH: true, // dummy data filled by backend on return
            countedLines: countedLines,
          };

          // send api call
          const requestOptions: RequestInit = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(savedDataSearchParams),
          };

          try {
            const response = await apiFetch(
              SAVED_DATA_ENDPOINT,
              requestOptions,
            );

            const responseReturn = response as StockCheckSavedDataSearch;
            if (responseReturn.countedLines) {
              responseReturn.countedLines.forEach((line) => {
                const locallySavedLine = countedLines.find((l) => {
                  return l.partNo == line.partNo;
                });

                const lineToAdd: StockCheckExtractLine = {
                  partNo: line.partNo,
                  partDescription: locallySavedLine?.partDescription ?? "",
                  binLocation: locallySavedLine?.binLocation ?? "",
                  stockQuantity: responseReturn.canSeeQoH ? 0 : null,
                  quantityCounted: locallySavedLine?.previousSystemProvided
                    ? locallySavedLine.previousSystemProvided
                    : 0,
                  systemProvided: locallySavedLine?.previousSystemProvided
                    ? locallySavedLine.previousSystemProvided
                    : 0,
                  shortShip: null,
                  wipQuantity: null,
                  expandTrigger: false,
                  status: "No Input",
                  wasAlreadyCounted: null,
                  qtySubmitted: locallySavedLine
                    ? locallySavedLine.locallyCounted
                    : null,
                  hasUnpublishedChanges: true,
                  hasUnsavedChanges: true,
                  indicatorStatus: "None",
                  rangeStart: 0,
                  index: 0,
                  partType: "Normal",
                  isNegativeStock: false,
                };

                addToFinishLines(lineToAdd);
              });
            }
          } catch {
            console.log("Error fetching saved data lines");
          } finally {
            setIsSavedDataLoaded(true);
          }
        }
      } else {
        // no saved data, load lines
        setIsSavedDataLoaded(true);
        initialGetLines();
      }
    }
  }

  useEffect(() => {
    if (isSavedDataLoaded && linesCollection.length == 0 && parentExtract) {
      initialGetLines();
      setIsSavedDataLoaded(false);
    }
  }, [isSavedDataLoaded, finishLines, parentExtract]);

  useEffect(() => {
    //filters updated
    selectDisplayLines(allLinesCollectionRef.current);
  }, [scLinesSearchRef.current]);

  async function getLines(
    RangeStart: number,
    ParentExtract: StockCheckExtract,
    SCLineSearch: StockCheckLinesSearch,
    isUpdate?: boolean,
  ): Promise<StockCheckExtractLine[]> {
    if (!beyondReturnLimit || (isUpdate && isFetchingRef.current == false)) {
      setShowLoadingLinesPage(true);
      isFetchingRef.current = true;

      if (RangeStart > 0 && ParentExtract.lines) {
        if (ParentExtract.lines[ParentExtract.lines.length - 1]) {
          ParentExtract.lastRecord =
            ParentExtract.lines[ParentExtract.lines.length - 1].partNo;
        }
      }

      if (RangeStart == 0 && ParentExtract) {
        ParentExtract.lastRecord = "";
      }

      // set search parameters
      scLinesSearchRef.current = {
        StockCheckExtract: {
          ...ParentExtract,
          stockCheckExtractRef: (
            "00000" + ParentExtract.stockCheckExtractRef
          ).slice(-5), // LeftPad the StockCheckRef to 5 chars with leading 0s so that SerialisedIsam fetches are not inhibited.
          lastRecord: lastRecordRef.current,
        },
        FetchAmount: fetchAmount,
        LastRecord: lastRecordRef.current,
        SearchTerm: searchTerm,
        Order: StockLineSearchOrderOption.BinLocation,
        OrderDirection: SCLineSearch.OrderDirection,
        FromBin: SCLineSearch.FromBin,
        ToBin: SCLineSearch.ToBin,
        CountedLineFilter: Inclusivity.Shown,
        SubmittedLineFilter: Inclusivity.Shown,
      };

      // send api call
      const requestOptions: RequestInit = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...scLinesSearchRef.current,
          stockCheckExtractRef: (
            "00000" + ParentExtract.stockCheckExtractRef
          ).slice(-5),
          // LeftPad the StockCheckRef to 5 chars with leading 0s so that SerialisedIsam fetches are not inhibited.
        }),
      };

      try {
        const response = await apiFetch(SEARCH_ENDPOINT, requestOptions);
        const responseExtract = response as StockCheckExtract;

        if (
          responseExtract.lines &&
          parentExtract &&
          responseExtract.lines.length > 0
        ) {
          rangeRef.current = {
            start: rangeRef.current.end,
            end: rangeRef.current.end + fetchAmount,
          };
          let endRef = false;

          if (responseExtract.lines.length < fetchAmount) {
            endRef = true;
          }

          lastRecordRef.current =
            responseExtract.lines[responseExtract.lines.length - 1].partNo;

          responseExtract.lines = handleLineIncomingOperation(
            responseExtract.lines,
          );

          responseExtract.lines = handleLineSavedData(responseExtract.lines);
          responseExtract.lines = handleFilterGetLines(responseExtract.lines);

          setParentExtract(responseExtract);

          fetchingLinesCollectionRef.current =
            fetchingLinesCollectionRef.current.concat(responseExtract.lines);
          if (
            endRef ||
            fetchingLinesCollectionRef.current.length ==
              ParentExtract.totalLines
          ) {
            allLinesCollectionRef.current = fetchingLinesCollectionRef.current;
          } else {
            getLines(
              rangeRef.current.start,
              parentExtract,
              scLinesSearchRef.current,
            );
          }

          return responseExtract.lines;
        }
      } catch (error: unknown) {
        let message = error;
        if (error instanceof Error) {
          message = error.message;
          console.log("GetLines Exception Message: " + message);
          console.log("GetLines Inner Exception: ", error);
        }

        toast.error("Error Fetching Lines: " + message, {
          toastId: customToastId,
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
          transition: Slide,
        });
        await sleep(2000);
      } finally {
        if (allLinesCollectionRef.current.length > 0) {
          setInitialLoadingFinished(true);
          isFetchingRef.current = false;
        } else {
          doNavAway("/stockcheck");
        }
      }
    }
    // return empty set of lines
    return [];
  }

  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  const handleFilterGetLines = (
    lines: StockCheckExtractLine[],
  ): StockCheckExtractLine[] => {
    if (scLinesSearchRef.current.CountedLineFilter == Inclusivity.Only) {
      lines = lines.filter((line) => {
        return (
          line.hasUnsavedChanges ||
          finishLines.some((f) => f.partNo == line.partNo)
        );
      });
    } else if (
      scLinesSearchRef.current.CountedLineFilter == Inclusivity.Hidden
    ) {
      lines = lines.filter((line) => {
        return !finishLines.some((f) => f.partNo == line.partNo);
      });
    }

    return lines;
  };

  const handleLineIncomingOperation = (
    lines: StockCheckExtractLine[],
  ): StockCheckExtractLine[] => {
    lines = lines.filter((line) => {
      if (linesCollectionPartNosRef.current.indexOf(line.partNo) != -1) {
        return false;
      } else {
        return true;
      }
    });

    lines.forEach((line) => {
      line.qtySubmitted = null;
      line.hasUnpublishedChanges = false;
      line.systemProvided = line.quantityCounted;

      linesCollectionPartNosRef.current =
        linesCollectionPartNosRef.current.concat([line.partNo]);

      if (!line.wasAlreadyCounted) {
        line.wasAlreadyCounted = line.status != "No Input";
      }
      if (line.wasAlreadyCounted) {
        line.qtySubmitted = line.quantityCounted;
      }
    });

    return lines;
  };

  const handleLineSavedData = (
    lines: StockCheckExtractLine[],
  ): StockCheckExtractLine[] => {
    if (parentExtract) {
      // if the line exists in finish lines, perform the 'saved data operations'
      lines.forEach((line) => {
        const fLine = finishLines.find((f) => {
          return f.partNo == line.partNo;
        });
        if (fLine) {
          line.qtySubmitted = fLine.qtySubmitted;
          line.hasUnpublishedChanges = true;
          line.indicatorStatus =
            line.stockQuantity == line.qtySubmitted ? "Correct" : "Incorrect";
          fLine.stockQuantity = line.stockQuantity;
          removeFromFinishLines(fLine);
          addToFinishLines(line);
        }
      });
    }
    return lines;
  };

  const changeSearchTerm = (event: { target: { value: string } }) => {
    const term = event.target.value;
    setSearchTerm(term);
    scLinesSearchRef.current.SearchTerm = term;
  };

  const checkForReturn = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "Go" || e.key === "Next") {
      const searchBar = document.getElementById(
        "stockextract-input-search",
      ) as HTMLElement;
      searchBar.blur();
    }
  };

  const handleLineUpdate = (newLineValue: StockCheckExtractLine) => {
    if (parentExtract && parentExtract.lines) {
      loadLineIntoFinishLines(newLineValue);
    }
    updateParentExtractLinesCounted();
  };

  const loadLineIntoFinishLines = (lineToAdd: StockCheckExtractLine) => {
    // is that Line already in FinishLines?
    if (finishLines.find((e) => e.partNo === lineToAdd.partNo)) {
      // working Finish Line
      let updatedFinishLines: StockCheckExtractLine[];

      if (lineToAdd.status == "No Input" && !lineToAdd.hasUnpublishedChanges) {
        // the incoming Value means we should remove from FinishLines
        // there is now no-input, so it shouldn't be in FinishLines
        updatedFinishLines = finishLines.filter((e) => {
          if (e.partNo != lineToAdd.partNo) {
            return e;
          }
        });
      } else {
        // Update finishLines Lines collection with this new line as-amended
        updatedFinishLines = finishLines.map((e) => {
          if (e.partNo === lineToAdd.partNo) {
            e.qtySubmitted = lineToAdd.qtySubmitted;
            e.status = lineToAdd.status;
            return e;
          } else {
            return e;
          }
        });
      }
      // commit changes to FinishLines
      setFinishLines(updatedFinishLines);
    } else {
      // incoming value is not in FinishLines already

      if (!lineToAdd.wasAlreadyCounted || lineToAdd.hasUnpublishedChanges) {
        // add new line into FinishLines collection
        setFinishLines((prevFinishLines) => [...prevFinishLines, lineToAdd]);
      }
    }
  };

  const addToFinishLines = (line: StockCheckExtractLine) => {
    const oldLine = finishLines.find((f) => {
      return f.partNo == line.partNo;
    });
    if (oldLine != null) {
      setFinishLines((prevFinishLines) => [
        ...prevFinishLines.filter((f) => {
          return !(line.partNo == f.partNo);
        }),
        line,
      ]);
    } else {
      setFinishLines((prevFinishLines) => [...prevFinishLines, line]);
      setFinishLines((prevFinishLines) =>
        prevFinishLines.filter((filLine, i) => {
          // for each line, does it have an already existing part no
          const matchLine =
            prevFinishLines.find((f) => {
              return f.partNo == filLine.partNo;
            }) ?? filLine;
          if (prevFinishLines.indexOf(matchLine) == i) return true;
          return false;
        }),
      );
    }
  };

  const removeFromFinishLines = (line: StockCheckExtractLine) => {
    const updatedLines = finishLines.filter((f) => {
      return f.partNo != line.partNo;
    });
    setFinishLines(updatedLines);
  };

  const updateParentExtractLinesCounted = () => {
    if (parentExtract) {
      const newLinesCountedValue =
        parentExtract.lines?.filter(
          (l: StockCheckExtractLine) => l.qtySubmitted != null,
        ).length ?? 0;

      setParentExtract({
        ...parentExtract,
        linesCounted: newLinesCountedValue,
      });
      setTotalLinesCounted(newLinesCountedValue);
    }
  };

  function anyUnsavedLines(): boolean {
    if (finishLines) {
      return finishLines.some((e) => {
        return e.hasUnsavedChanges;
      });
    } else {
      return false;
    }
  }

  const preNavAway = (target: string) => {
    setTargetExitNavigation(target);
    setSearchTerm("");
    scLinesSearchRef.current.SearchTerm = "";
    if (parentExtract && parentExtract.lines) {
      if (anyUnsavedLines()) {
        setShowUnsavedChangesBox((prev) => !prev);
      } else {
        doNavAway(target);
      }
    } else {
      doNavAway(target);
    }
  };

  const doNavAway = (target: string) => {
    // unlock stock check
    apiFetch<StockCheckExtract>("/partsapp/stock/RemoveStockCheckUser", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(parentExtract),
    }).finally(() => {
      navigate(target);
    });
  };

  // handle filtering of the lines
  const filterListItems = (
    lines: StockCheckExtractLine[],
  ): StockCheckExtractLine[] => {
    // only take lines that satisfy the filters
    lines = handleFilterGetLines(lines);

    return lines;
  };

  const handleLineOrdering = (
    lines: StockCheckExtractLine[],
  ): StockCheckExtractLine[] => {
    if (orderDirectionRef.current == OrderDirection.Ascending) {
      lines = lines.sort((a, b) => {
        if (a.binLocation && b.binLocation) {
          return a.binLocation.localeCompare(b.binLocation);
        } else {
          return 0;
        }
      });
    } else {
      lines = lines.sort((a, b) => {
        if (a.binLocation && b.binLocation) {
          return b.binLocation.localeCompare(a.binLocation);
        } else {
          return 0;
        }
      });
    }

    return lines;
  };

  const handleBinFilterLines = (
    lines: StockCheckExtractLine[],
  ): StockCheckExtractLine[] => {
    if (scLinesSearchRef.current.FromBin != "") {
      if (scLinesSearchRef.current.ToBin != "") {
        lines = lines.filter((line) => {
          if (line.binLocation) {
            return (
              line.binLocation >= scLinesSearchRef.current.FromBin &&
              line.binLocation <= scLinesSearchRef.current.ToBin
            );
          } else {
            return false;
          }
        });
      } else {
        lines = lines.filter((line) => {
          if (line.binLocation) {
            return line.binLocation >= scLinesSearchRef.current.FromBin;
          } else {
            return false;
          }
        });
      }
    } else if (scLinesSearchRef.current.ToBin != "") {
      lines = lines.filter((line) => {
        if (line.binLocation) {
          return line.binLocation <= scLinesSearchRef.current.ToBin;
        } else {
          return false;
        }
      });
    }

    return lines;
  };

  const handleSearchTermFilter = (
    lines: StockCheckExtractLine[],
  ): StockCheckExtractLine[] => {
    const upperSearchTerm = scLinesSearchRef.current.SearchTerm.toUpperCase();
    lines = lines.filter(
      (line) =>
        line.partNo.includes(upperSearchTerm) ||
        line.partDescription?.includes(upperSearchTerm) ||
        line.binLocation?.includes(upperSearchTerm),
    );
    return lines;
  };

  const filterSubmittedLines = (
    lines: StockCheckExtractLine[],
  ): StockCheckExtractLine[] => {
    const setCounted = new Set(
      allLinesCollectionRef.current.filter(
        (line) => line.hasUnpublishedChanges,
      ),
    );
    const setSubmitted = new Set(
      allLinesCollectionRef.current.filter((line) => line.wasAlreadyCounted),
    );
    const setOther = new Set(
      allLinesCollectionRef.current.filter((line) => {
        return !(line.hasUnpublishedChanges || line.wasAlreadyCounted);
      }),
    );
    const filter = scLinesSearchRef.current;

    if (
      filter.CountedLineFilter == Inclusivity.Only &&
      filter.SubmittedLineFilter == Inclusivity.Only
    ) {
      lines = [...setCounted].filter((line) => setSubmitted.has(line));
    }

    if (
      filter.CountedLineFilter == Inclusivity.Shown &&
      filter.SubmittedLineFilter == Inclusivity.Only
    ) {
      lines = [...setSubmitted];
    }

    if (
      filter.CountedLineFilter == Inclusivity.Hidden &&
      filter.SubmittedLineFilter == Inclusivity.Only
    ) {
      lines = [...setSubmitted].filter(
        (line) => !setCounted.has(line) && !setOther.has(line),
      );
    }

    if (
      filter.CountedLineFilter == Inclusivity.Only &&
      filter.SubmittedLineFilter == Inclusivity.Shown
    ) {
      lines = [...setCounted];
    }

    if (
      filter.CountedLineFilter == Inclusivity.Shown &&
      filter.SubmittedLineFilter == Inclusivity.Shown
    ) {
      lines = [...lines];
    }

    if (
      filter.CountedLineFilter == Inclusivity.Hidden &&
      filter.SubmittedLineFilter == Inclusivity.Shown
    ) {
      lines = [...lines].filter((line) => !setCounted.has(line));
    }

    if (
      filter.CountedLineFilter == Inclusivity.Only &&
      filter.SubmittedLineFilter == Inclusivity.Hidden
    ) {
      lines = [...setCounted].filter((line) => !setSubmitted.has(line));
    }

    if (
      filter.CountedLineFilter == Inclusivity.Shown &&
      filter.SubmittedLineFilter == Inclusivity.Hidden
    ) {
      lines = [...lines].filter((line) => !setSubmitted.has(line));
    }

    if (
      filter.CountedLineFilter == Inclusivity.Hidden &&
      filter.SubmittedLineFilter == Inclusivity.Hidden
    ) {
      lines = [...setOther];
    }

    return lines;
  };

  useEffect(() => {
    if (allLinesCollectionRef.current.length > 0) {
      setShowLoadingLinesPage(false);
    }
    selectDisplayLines(allLinesCollectionRef.current);
  }, [allLinesCollectionRef.current, scLinesSearchRef.current.SearchTerm]);

  // filter and select lines for display on the screen
  const selectDisplayLines = (lines: StockCheckExtractLine[]) => {
    lines = handleFilterGetLines(lines);
    lines = filterSubmittedLines(lines);
    lines = handleBinFilterLines(lines);
    lines = handleSearchTermFilter(lines);
    lines = handleLineOrdering(lines);

    if (lines.length == 0 && initialLoadingFinished) {
      toast.warn("No Results Found", {
        toastId: customToastId,
        position: "bottom-center",
        autoClose: 1000,
        theme: "colored",
        transition: Slide,
      });
    }

    setDisplayLines(lines);
  };

  return (
    <>
      <StockExtractFinishPage
        finishLines={finishLines}
        parentExtract={parentExtract}
        showFinishPage={showFinishPage}
        searchIconClicked={function (line: StockCheckExtractLine): void {
          if (parentExtract) {
            setSearchTerm(line.partNo);
            scLinesSearchRef.current.SearchTerm = line.partNo;
            const scrollList = document.getElementById("scrollable-large-list");
            if (scrollList) {
              scrollList.scrollTop = 0;
            }
          }

          setShowFinishPage(false);
        }}
        onCancel={() => setShowFinishPage(false)}
      />
      <StockUnsavedChanges
        showUnsavedChanges={showUnsavedChangesBox}
        onNavigate={() => doNavAway(targetExitNavigation)}
        onCancel={() => setShowUnsavedChangesBox(false)}
      />
      <StockLoadingLines
        showLoadingLinesModal={showLoadingLinesPage}
        showProgressBar={showProgressBar}
        totalLines={progressTotalLines.current}
        fetchAmount={fetchAmount}
        completedLines={fetchingLinesCollectionRef.current.length}
        stockCheckRef={parentExtractRef.current}
      />
      {searchAndFilterMenuVisible ? (
        <div id="main-menu-modal-mask" className="modal-mask"></div>
      ) : null}
      <div id="stockextract-container" className="module-container">
        <div id="stockextract-nav-top" className="module-nav nav-top">
          <div
            id="stockextract-nav-top-container"
            className="module-nav-button top"
          >
            <AbstractButton
              id="stockextract-nav-back"
              text="Back"
              icon={<BackIcon className="module-nav-button-logo" />}
              disabled={false}
              activeColor={ForegroundColorSet.Blue}
              background={BackgroundColorSet.Black1}
              specialClass={null}
              actionCallback={handleBackPress}
            />
          </div>

          <div className="module-nav-button top">
            <AbstractButton
              id="stockextract-nav-sort-and-filter"
              text="Filter"
              icon={<SortIcon className="module-nav-button-logo" />}
              disabled={false}
              activeColor={ForegroundColorSet.Blue}
              background={BackgroundColorSet.Black1}
              specialClass={null}
              actionCallback={showSortandFilter}
            />
          </div>

          <div className="module-nav-button top">
            <AbstractButton
              id="stockcheck-nav-add-line"
              text="Save"
              icon={<SaveIcon className="module-nav-button-logo" />}
              disabled={!anyUnsavedLines()}
              activeColor={ForegroundColorSet.Blue}
              background={BackgroundColorSet.Black1}
              specialClass={null}
              actionCallback={handleSavePress}
            />
          </div>

          <div className="module-nav-button top">
            <AbstractButton
              id="stockcheck-nav-complete"
              text="Finish"
              icon={<CompleteIcon className="module-nav-button-logo" />}
              disabled={
                finishLines.length == 0 ||
                fetchingLinesCollectionRef.current.length == 0
              }
              activeColor={ForegroundColorSet.Green}
              background={BackgroundColorSet.Black1}
              specialClass={null}
              actionCallback={handleCompletePress}
            />
          </div>
        </div>

        <div id="stockextract-tab-container" className="search-results">
          <TabList tabs={tabList} twoOptions={true} isDisabled={false} />
          <div className="stockextract-tab-page-header-line">
            <div className="stockextract-tab-page-header-sc">
              {Number(parentExtract?.stockCheckExtractRef)}
              &nbsp;-&nbsp;
              {parentExtract?.stockCheckExtractDesc
                ? parentExtract.stockCheckExtractDesc
                : "No Description"}
            </div>
          </div>
        </div>

        <div
          id="stockextract-content"
          className="stockextract-search-results pos-lines"
        >
          <ScrollableLargeList
            renderItem={(line) => (
              <StockExtractLineCard
                key={displayLines.indexOf(line) + "-" + line.partNo}
                sce={location.state.sce}
                sceLine={line}
                updateLine={handleLineUpdate}
                addToFinishLines={addToFinishLines}
                removeFromFinishLines={removeFromFinishLines}
              />
            )}
            filterListItems={filterListItems}
            listItems={displayLines}
            itemHeight={window.screen.availHeight * 0.106}
            containerHeight={window.screen.availHeight * 0.55}
          />
        </div>

        <div
          id="stockextract-input-content"
          className="module-search search-input"
        >
          <input
            id="stockextract-input-search"
            className="module-search-term-input-text"
            type="text"
            onKeyDown={checkForReturn}
            onChange={changeSearchTerm}
            value={searchTerm}
            placeholder="&#x1F50D; Search..."
            autoCorrect="off"
            autoCapitalize="none"
            autoComplete="off"
            spellCheck="false"
          />
          <button
            id="stockextract-search-barcode-button"
            className="module-search-barcode-button greyed-out"
          >
            <ScanIcon className="module-search-barcode-button-logo" />
          </button>
        </div>
      </div>
      <div
        id="stockextract-search-input-curtain"
        className="search-bar-safe-area-curtain"
      />
      <ToastContainer />
      <NavBar activePage="check" navigationCallBack={preNavAway} />

      <StockCheckLineFilter
        onShowTrigger={showSearchAndFilterMenuTrigger}
        onOkayCallback={updateFilter}
        onCancelCallback={cancelFilterButtonClicked}
        visibilityClassName={SearchAndFilterClasses}
        searchData={scLinesSearchRef.current}
      />
    </>
  );
};

export default StockExtract;
